/**
 * PresetManagementPage - Manage your uploaded presets
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ChevronLeft,
  Package,
  DollarSign,
  Edit2,
  Trash2,
  Eye,
  Download,
  Star,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Search,
  Filter,
  MoreVertical,
  Save,
  X,
  Music,
  Sliders,
  TrendingUp,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Price tiers configuration
const PRICE_TIERS = {
  free: { label: 'Free', price: 0 },
  basic: { label: 'Basic', price: 2.99 },
  standard: { label: 'Standard', price: 4.99 },
  premium: { label: 'Premium', price: 9.99 },
  professional: { label: 'Professional', price: 19.99 }
};

// Preset Row Component
const PresetRow = ({ preset, onEdit, onDelete, onUpdatePrice }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedTier, setSelectedTier] = useState(preset.price_tier || 'free');
  const [isSaving, setIsSaving] = useState(false);
  
  const handleSavePrice = async () => {
    setIsSaving(true);
    try {
      await onUpdatePrice(preset.id, selectedTier);
      setIsEditing(false);
      toast.success('Price updated successfully');
    } catch (error) {
      toast.error('Failed to update price');
    } finally {
      setIsSaving(false);
    }
  };
  
  const getStatusBadge = () => {
    switch (preset.status) {
      case 'approved':
        return (
          <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-500/20 text-emerald-400 flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Published
          </span>
        );
      case 'pending':
        return (
          <span className="px-2 py-0.5 rounded-full text-xs bg-amber-500/20 text-amber-400 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Pending Review
          </span>
        );
      case 'rejected':
        return (
          <span className="px-2 py-0.5 rounded-full text-xs bg-red-500/20 text-red-400 flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            Rejected
          </span>
        );
      default:
        return null;
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4"
    >
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
          preset.preset_type === 'master'
            ? 'bg-amber-500/20'
            : 'bg-cyan-500/20'
        }`}>
          {preset.preset_type === 'master' ? (
            <Sliders className="w-6 h-6 text-amber-400" />
          ) : (
            <Music className="w-6 h-6 text-cyan-400" />
          )}
        </div>
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-white truncate">{preset.name}</h3>
            {getStatusBadge()}
          </div>
          <p className="text-sm text-zinc-400 line-clamp-1 mb-2">
            {preset.description || 'No description'}
          </p>
          
          {/* Stats */}
          <div className="flex items-center gap-4 text-xs text-zinc-500">
            <span className="flex items-center gap-1">
              <Download className="w-3 h-3" />
              {preset.download_count || 0} downloads
            </span>
            <span className="flex items-center gap-1">
              <Star className="w-3 h-3" />
              {preset.average_rating?.toFixed(1) || '0.0'} ({preset.rating_count || 0})
            </span>
            <span className="flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              {preset.upvote_count || 0} upvotes
            </span>
          </div>
        </div>
        
        {/* Price Section */}
        <div className="flex items-center gap-3">
          {isEditing ? (
            <div className="flex items-center gap-2">
              <select
                value={selectedTier}
                onChange={(e) => setSelectedTier(e.target.value)}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-violet-500"
                data-testid={`price-select-${preset.id}`}
              >
                {Object.entries(PRICE_TIERS).map(([key, tier]) => (
                  <option key={key} value={key}>
                    {tier.price === 0 ? 'Free' : `$${tier.price.toFixed(2)}`}
                  </option>
                ))}
              </select>
              <Button
                size="sm"
                onClick={handleSavePrice}
                disabled={isSaving}
                className="bg-emerald-600 hover:bg-emerald-500"
                data-testid={`save-price-${preset.id}`}
              >
                {isSaving ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSelectedTier(preset.price_tier || 'free');
                  setIsEditing(false);
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                (preset.price || 0) === 0
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : 'bg-violet-500/20 text-violet-400'
              }`}>
                {(preset.price || 0) === 0 ? 'FREE' : `$${(preset.price || 0).toFixed(2)}`}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setIsEditing(true)}
                className="text-zinc-400 hover:text-white"
                data-testid={`edit-price-${preset.id}`}
              >
                <Edit2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => window.open(`/marketplace/${preset.id}`, '_blank')}
            className="text-zinc-400 hover:text-white"
            title="View in Marketplace"
          >
            <Eye className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete(preset.id)}
            className="text-zinc-400 hover:text-red-400"
            title="Delete Preset"
            data-testid={`delete-preset-${preset.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default function PresetManagementPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    published: 0,
    pending: 0,
    totalDownloads: 0,
    totalEarnings: 0
  });
  
  // Fetch presets
  const fetchPresets = useCallback(async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/marketplace/user/${user.id}/presets`);
      if (response.ok) {
        const data = await response.json();
        setPresets(data.presets || []);
        
        // Calculate stats
        const published = data.presets?.filter(p => p.status === 'approved').length || 0;
        const pending = data.presets?.filter(p => p.status === 'pending').length || 0;
        const totalDownloads = data.presets?.reduce((sum, p) => sum + (p.download_count || 0), 0) || 0;
        
        setStats({
          total: data.presets?.length || 0,
          published,
          pending,
          totalDownloads,
          totalEarnings: 0 // Would come from payments API
        });
      }
    } catch (error) {
      console.error('Failed to fetch presets:', error);
      toast.error('Failed to load presets');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);
  
  // Initial load
  useEffect(() => {
    fetchPresets();
  }, [fetchPresets]);
  
  // Update preset price
  const handleUpdatePrice = async (presetId, priceTier) => {
    try {
      const response = await fetch(`${API_URL}/api/payments/preset/${presetId}/price`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          preset_id: presetId,
          price_tier: priceTier
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update price');
      }
      
      const data = await response.json();
      
      // Update local state
      setPresets(prev => prev.map(p => 
        p.id === presetId 
          ? { ...p, price_tier: priceTier, price: data.price }
          : p
      ));
      
      return data;
    } catch (error) {
      console.error('Price update error:', error);
      throw error;
    }
  };
  
  // Delete preset
  const handleDelete = async (presetId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/marketplace/presets/${presetId}?user_id=${user.id}`,
        { method: 'DELETE' }
      );
      
      if (response.ok) {
        setPresets(prev => prev.filter(p => p.id !== presetId));
        toast.success('Preset deleted');
        setDeleteConfirm(null);
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      toast.error('Failed to delete preset');
      console.error(error);
    }
  };
  
  // Filter presets
  const filteredPresets = presets.filter(preset => {
    const matchesSearch = !searchQuery || 
      preset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      preset.description?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || preset.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });
  
  // Redirect if not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <p className="text-zinc-400 mb-4">Please log in to manage your presets</p>
          <Button onClick={() => navigate('/')} className="bg-violet-600 hover:bg-violet-500">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <div className="bg-zinc-900/80 border-b border-zinc-800 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/marketplace')}
                className="text-zinc-400 hover:text-white"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  <Package className="w-5 h-5 text-violet-400" />
                  My Presets
                </h1>
                <p className="text-sm text-zinc-400">Manage your uploaded presets and pricing</p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/marketplace/upload')}
              className="bg-violet-600 hover:bg-violet-500"
            >
              Upload New Preset
            </Button>
          </div>
        </div>
      </div>
      
      {/* Stats */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
            <div className="text-2xl font-bold text-white">{stats.total}</div>
            <div className="text-sm text-zinc-400">Total Presets</div>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
            <div className="text-2xl font-bold text-emerald-400">{stats.published}</div>
            <div className="text-sm text-zinc-400">Published</div>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
            <div className="text-2xl font-bold text-amber-400">{stats.pending}</div>
            <div className="text-sm text-zinc-400">Pending Review</div>
          </div>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
            <div className="text-2xl font-bold text-cyan-400">{stats.totalDownloads}</div>
            <div className="text-sm text-zinc-400">Total Downloads</div>
          </div>
        </div>
        
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search presets..."
              className="pl-10 bg-zinc-900/50 border-zinc-800"
              data-testid="search-presets"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-zinc-900/50 border border-zinc-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-violet-500"
            data-testid="status-filter"
          >
            <option value="all">All Status</option>
            <option value="approved">Published</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        
        {/* Preset List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-zinc-900/50 border border-zinc-800 rounded-xl h-24 animate-pulse" />
            ))}
          </div>
        ) : filteredPresets.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-zinc-400 mb-2">
              {searchQuery || statusFilter !== 'all' ? 'No presets found' : 'No presets yet'}
            </h3>
            <p className="text-zinc-500 mb-4">
              {searchQuery || statusFilter !== 'all' 
                ? 'Try adjusting your filters'
                : 'Upload your first preset to start earning!'}
            </p>
            {!searchQuery && statusFilter === 'all' && (
              <Button
                onClick={() => navigate('/marketplace/upload')}
                className="bg-violet-600 hover:bg-violet-500"
              >
                Upload First Preset
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredPresets.map(preset => (
              <PresetRow
                key={preset.id}
                preset={preset}
                onUpdatePrice={handleUpdatePrice}
                onDelete={(id) => setDeleteConfirm(id)}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setDeleteConfirm(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                  <Trash2 className="w-6 h-6 text-red-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Delete Preset?</h3>
                <p className="text-zinc-400 mb-6">
                  This action cannot be undone. The preset will be permanently removed from the marketplace.
                </p>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setDeleteConfirm(null)}
                    className="flex-1 border-zinc-700"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleDelete(deleteConfirm)}
                    className="flex-1 bg-red-600 hover:bg-red-500"
                    data-testid="confirm-delete"
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
